import { hitProtectedBackendApi } from "@/app/endpoints/enpointsUtils"
import {
    ITravelQuote,
    ITravelQuoteRequest,
    ITravelQuotesResponse,
    ITravelSubscriptionRequest,
    ITravelSubscriptionResponse,
} from "@/app/models/travelComparator"

const BASE_API = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/travel`

const ENDPOINTS = {
    CREATE_QUOTE: `${BASE_API}/quotes/create`,
    QUOTE: (token: string) => `${BASE_API}/quotes/${token}`,
    QUOTE_OFFERS: (token: string) => `${BASE_API}/quotes/${token}/offers`,
    SUBSCRIPTIONS: `${BASE_API}/subscriptions`,
    SUBSCRIPTION: (token: string) => `${BASE_API}/subscriptions/${token}`,
}

export interface TravelSubscriberPatch {
    gender?: number | null
    firstName?: string
    lastName?: string
    email?: string
    phone?: string | null
    birthdate?: string
    address?: string | null
    city?: string | null
    zip?: string | null
    nationality?: string | null
}

export interface TravelPassengerPatch {
    firstName?: string
    lastName?: string
    birthdate?: string
    passport?: string | null
}

export type TravelQuotePatch = Partial<Omit<ITravelQuoteRequest, "tripAmountPerPerson">> & {
    funnelStep?: string
    subscriber?: TravelSubscriberPatch
    passengers?: TravelPassengerPatch[]
    selectedOffer?: number | null
    tripAmountPerPerson?: number | null
    clickIds?: { gclid?: string; fbclid?: string; msclkid?: string; ttclid?: string }
}

export async function createTravelQuote(): Promise<ITravelQuote> {
    const response = await hitProtectedBackendApi(ENDPOINTS.CREATE_QUOTE, { method: "POST" })

    if (!response.ok) {
        throw new Error(`Erreur lors de la création du devis (${response.status})`)
    }

    return await response.json()
}

export async function getTravelQuoteFromToken(token: string): Promise<ITravelQuote> {
    const response = await hitProtectedBackendApi(ENDPOINTS.QUOTE(token))

    if (!response.ok) {
        throw new Error(`Devis introuvable (${response.status})`)
    }

    return await response.json()
}

export async function patchTravelQuote(token: string, patch: TravelQuotePatch): Promise<ITravelQuote> {
    const response = await hitProtectedBackendApi(ENDPOINTS.QUOTE(token), { method: "PATCH" }, patch)

    if (!response.ok) {
        const data = await response.json().catch(() => null)
        throw new Error(data?.message || `Erreur lors de la mise à jour du devis (${response.status})`)
    }

    return await response.json()
}

export async function getTravelOffers(token: string): Promise<ITravelQuotesResponse> {
    const response = await hitProtectedBackendApi(ENDPOINTS.QUOTE_OFFERS(token))

    if (!response.ok) {
        throw new Error(`Erreur lors de la récupération des offres (${response.status})`)
    }

    return await response.json()
}

export async function postTravelSubscription(
    payload: ITravelSubscriptionRequest
): Promise<ITravelSubscriptionResponse> {
    const response = await hitProtectedBackendApi(ENDPOINTS.SUBSCRIPTIONS, { method: "POST" }, payload)

    if (!response.ok) {
        const data = await response.json().catch(() => null)
        throw new Error(data?.message || `Erreur lors de la souscription (${response.status})`)
    }

    return await response.json()
}

export async function getTravelSubscription(token: string): Promise<ITravelSubscriptionResponse> {
    const response = await hitProtectedBackendApi(ENDPOINTS.SUBSCRIPTION(token))

    if (!response.ok) {
        throw new Error(`Souscription introuvable (${response.status})`)
    }

    return await response.json()
}
